/**
 * The in-game HUD over the canvas, in the style of the app shell: top bar (menu, level,
 * race progress strip, timer, microphone), boost buttons, notices and the overlays.
 *
 * The container ignores pointers (presses go through to the play area); only controls take
 * them, and they carry CONTROL_ATTR so a tap on a button is never a jump. Everything sits
 * inside the safe area; the reconnect pill (top centre) and toasts (bottom centre) of the
 * shell keep their spots free.
 *
 * A phone held upright gets the rotate hint over the game, but the top bar stays above it:
 * a phone that does not turn (rotation lock; iOS ignores the app's orientation) must still
 * reach the menu to give up or leave.
 */
import type { PlayerId } from '@cube-rush/shared';
import { useMemo } from 'preact/hooks';
import { ru } from '../../i18n/ru';
import { CONTROL_ATTR } from '../../input/GameInput';
import { isIos } from '../../pwa/platform';
import type { GameVoiceBridge } from '../contract';
import { formatTimerLeft } from '../raceClock';
import type { HudActions } from './actions';
import { BoostBar } from './BoostBar';
import type { HudDom } from './hudDom';
import type { HudModel } from './hudModel';
import {
  Countdown,
  FinishCard,
  GameMenu,
  LevelFlash,
  MainViewLayer,
  ModeBanner,
  Notices,
  RotateHint,
  SpectatorBar,
  TimeUpCard,
  ViewLabels,
} from './overlays';
import { RaceStrip } from './RaceStrip';
import './hud.css';

export interface HudProps {
  hud: HudModel;
  dom: HudDom;
  actions: HudActions;
  voice: GameVoiceBridge;
  /** Name and avatar emoji of a racer. */
  nameOf(id: PlayerId): { name: string; avatar: string };
}

const control = { [CONTROL_ATTR]: '' };
/** Height of the top bar, so view names stay below it. */
const TOP_BAR = 68;

export function Hud({ hud, dom, actions, voice, nameOf }: HudProps) {
  const touch = useMemo(
    () => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches,
    [],
  );
  const ios = useMemo(() => typeof navigator === 'object' && isIos(navigator), []);
  const phase = hud.phase.value;
  const racing = !hud.spectator;
  const showLevel = racing || hud.watching.value !== null;
  const rotate = hud.rotateHint.value;
  return (
    <div class={`hud${touch ? ' hud--touch' : ''}${rotate ? ' hud--rotate' : ''}`}>
      <div
        class="hud__top"
        ref={(element) => {
          dom.topBar = element;
        }}
      >
        <div class="hud__cluster">
          <button
            type="button"
            class="hud-icon-btn hud-icon-btn--menu"
            aria-label={ru.game.menu}
            aria-expanded={hud.menuOpen.value}
            {...control}
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => actions.setMenu(!hud.menuOpen.peek())}
          >
            <span aria-hidden="true">☰</span>
          </button>
          {showLevel && <LevelBadge hud={hud} dom={dom} />}
        </div>
        <RaceStrip hud={hud} dom={dom} speaking={voice.speaking} />
        <div class="hud__cluster hud__cluster--right">
          {/* By levels the race time runs up; against the clock the time left runs down. */}
          <div
            class={`hud-timer panel${hud.hurry.value ? ' hud-timer--hurry' : ''}`}
            title={hud.timed ? ru.game.timeLeft : ru.game.raceTime}
          >
            <span
              ref={(element) => {
                dom.timer = element;
              }}
            >
              {hud.durationMs === null ? '0:00.0' : formatTimerLeft(hud.durationMs)}
            </span>
          </div>
          <MicButton voice={voice} actions={actions} />
        </div>
      </div>
      {!rotate && <Notices hud={hud} />}
      <ViewLabels hud={hud} topInset={TOP_BAR} />
      {racing && phase !== 'finished' && phase !== 'timeUp' && (
        <BoostBar hud={hud} dom={dom} actions={actions} />
      )}
      <SpectatorBar hud={hud} dom={dom} actions={actions} nameOf={nameOf} />
      {/* Never under the hint (they would show through it): back, as they are by then, once the
          phone is turned. */}
      {!rotate && (
        <MainViewLayer hud={hud}>
          <Countdown hud={hud} touch={touch} />
          <LevelFlash hud={hud} />
          <ModeBanner hud={hud} touch={touch} />
          <FinishCard hud={hud} dom={dom} />
          <TimeUpCard hud={hud} dom={dom} />
        </MainViewLayer>
      )}
      {rotate && <RotateHint ios={ios} />}
      <GameMenu hud={hud} actions={actions} />
    </div>
  );
}

function LevelBadge({ hud, dom }: { hud: HudModel; dom: HudDom }) {
  const level = Math.min(hud.level.value + 1, hud.levelCount);
  const total = hud.levelTotal;
  return (
    // Against the clock there is no last level: «Уровень 3», the word shown even on phones.
    <div class={`hud-level panel${total === null ? ' hud-level--open' : ''}`}>
      {/* The spaces only keep the words apart in the text («Уровень 3 Попытка 2»). */}
      <span class="hud-level__label">{ru.game.level}</span>{' '}
      <span class="hud-level__value">
        {total === null ? String(level) : ru.game.levelOf(level, total)}
      </span>{' '}
      {!hud.spectator && (
        <span class="hud-level__attempt">{ru.game.attempt(hud.attempt.value)}</span>
      )}
      <span class="hud-level__bar" aria-hidden="true">
        <i
          ref={(element) => {
            dom.levelBar = element;
          }}
        />
      </span>
    </div>
  );
}

function MicButton({ voice, actions }: { voice: GameVoiceBridge; actions: HudActions }) {
  const available = voice.micAvailable.value;
  const muted = voice.muted.value;
  if (!available && voice.micLost.value) {
    // Lost to a call or the background, not refused: a tap asks for it again.
    return (
      <button
        type="button"
        class="hud-icon-btn hud-icon-btn--off"
        aria-label={ru.voice.unmute}
        {...control}
        onPointerDown={(event) => event.preventDefault()}
        onClick={() => actions.retryMic()}
      >
        <span aria-hidden="true">🚫</span>
      </button>
    );
  }
  if (!available) {
    return (
      <span class="hud-icon-btn hud-icon-btn--off" role="img" aria-label={ru.voice.unavailable}>
        <span aria-hidden="true">🚫</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      class={`hud-icon-btn ${muted ? 'hud-icon-btn--muted' : 'hud-icon-btn--live'}`}
      aria-label={muted ? ru.voice.unmute : ru.voice.mute}
      {...control}
      onPointerDown={(event) => event.preventDefault()}
      onClick={() => actions.toggleMute()}
    >
      <span aria-hidden="true">{muted ? '🔇' : '🎤'}</span>
    </button>
  );
}
